// Pure CSV parsing and bank-export column mapping. No React, no Supabase.
// Mercury's export is the first target, but every bank's file is a little
// different, so detection is fuzzy and the person can override it.
import type { BankImportRow } from './types';

/** RFC 4180: quoted fields, doubled quotes, CRLF, newlines inside quotes. Drops blank rows. */
export function parseCsv(input: string): string[][] {
  let text = input;
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const n = text.length;
  let i = 0;
  while (i < n) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') {
      i++;
      if (text[i] === '\n') continue; // CRLF: the \n ends the row
      row.push(field); rows.push(row); row = []; field = '';
      continue;
    }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c;
    i++;
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

export type ColumnMap = {
  date?: number;
  amount?: number;
  description?: number;
  counterparty?: number;
  id?: number;
  debit?: number;
  credit?: number;
};

export const COLUMN_LABELS: Record<keyof ColumnMap, string> = {
  date: 'Date',
  amount: 'Amount (signed)',
  credit: 'Money in',
  debit: 'Money out',
  description: 'Description',
  counterparty: 'Counterparty',
  id: 'Transaction id',
};

const normalize = (h: string) => h.trim().toLowerCase().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ');

function pick(headers: string[], patterns: RegExp[], taken: Set<number>): number | undefined {
  for (const re of patterns) {
    const idx = headers.findIndex((h, i) => !taken.has(i) && re.test(h));
    if (idx >= 0) { taken.add(idx); return idx; }
  }
  return undefined;
}

/** Fuzzy header match. Mercury: "Date (UTC)", "Description", "Amount", "Bank Description". */
export function detectColumns(header: string[]): ColumnMap {
  const h = header.map(normalize);
  const taken = new Set<number>();
  const map: ColumnMap = {};

  map.date = pick(h, [/^date$/, /^date \(/, /^posted/, /^post date/, /^transaction date/, /^booking date/, /^value date/, /date/, /^timestamp/], taken);
  map.debit = pick(h, [/^debit/, /^withdrawal/, /money out/, /^paid out/, /^outflow/, /^spent/, /^payments?$/], taken);
  map.credit = pick(h, [/^credit(?! card)/, /^deposit/, /money in/, /^paid in/, /^inflow/, /^received/, /^receipts?$/], taken);
  map.amount = pick(h, [/^amount$/, /^amount \(/, /^transaction amount/, /^net amount/, /^amount/, /amount/, /^value$/, /^total$/], taken);
  map.id = pick(h, [/transaction id/, /^id$/, /^txn id/, /^transaction number/, /^transaction ref/, /^reference (number|id|no)/, /^ref (number|id|no)/, /^fitid/, /^unique id/, /^trace/], taken);
  map.description = pick(h, [/^bank description/, /^description/, /^memo/, /^details/, /^narrative/, /^transaction details/, /^particulars/, /description/, /memo/], taken);
  map.counterparty = pick(h, [/^counter ?party/, /^payee/, /^merchant( name)?$/, /^vendor/, /^name$/, /^beneficiary/, /^payer/, /^counter ?party/], taken);
  // Mercury: "Bank Description" is the raw bank text and "Description" is the cleaned-up name.
  if (map.counterparty == null && map.description != null && /^bank description/.test(h[map.description])) {
    map.counterparty = pick(h, [/^description$/], taken);
  }
  return map;
}

/** "$1,234.56" → 123456; "(12.34)" → −1234; "-12.34" and "12.34-" → −1234. Null when unreadable. */
export function parseAmountCents(input: string): number | null {
  let s = (input ?? '').trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1).trim(); }
  s = s.replace(/[−–—]/g, '-');
  if (s.startsWith('-')) { neg = true; s = s.slice(1); }
  if (s.endsWith('-')) { neg = true; s = s.slice(0, -1); }
  if (/^\+/.test(s)) s = s.slice(1);
  if (/\bdr\b/i.test(s)) neg = true;
  s = s.replace(/[^0-9.]/g, '');
  if (!s || s === '.') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const cents = Math.round(n * 100);
  return neg ? -cents : cents;
}

function buildIso(y: number, m: number, d: number): string | null {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Accepts YYYY-MM-DD (with or without a time), YYYY/MM/DD, M/D/YYYY, M/D/YY, M-D-YYYY, "Oct 3, 2026". */
export function toIsoDate(input: string): string | null {
  const s = (input ?? '').trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]|$)/.exec(s);
  if (m) return buildIso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})(?:[T\s]|$)/.exec(s);
  if (m) return buildIso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4}|\d{2})(?:[T\s]|$)/.exec(s);
  if (m) {
    const yy = Number(m[3]);
    const y = m[3].length === 2 ? 2000 + yy : yy;
    return buildIso(y, Number(m[1]), Number(m[2]));
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return buildIso(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return null;
}

/**
 * Mapped rows → what llc_bank_import takes. Rows without a readable date or
 * amount are skipped. A separate Money out column is negative; Money in positive.
 * `raw` keeps the whole original row keyed by header.
 */
export function toImportRows(rows: string[][], map: ColumnMap, header: string[] = []): BankImportRow[] {
  const out: BankImportRow[] = [];
  const cell = (r: string[], idx: number | undefined) => (idx == null ? '' : (r[idx] ?? '').trim());
  for (const r of rows) {
    const posted_date = toIsoDate(cell(r, map.date));
    let amount_cents: number | null = null;
    if (map.amount != null) amount_cents = parseAmountCents(cell(r, map.amount));
    if (amount_cents == null && (map.debit != null || map.credit != null)) {
      const d = map.debit != null ? parseAmountCents(cell(r, map.debit)) : null;
      const c = map.credit != null ? parseAmountCents(cell(r, map.credit)) : null;
      if (d != null || c != null) amount_cents = Math.abs(c ?? 0) - Math.abs(d ?? 0);
    }
    if (!posted_date || amount_cents == null) continue;
    const counterparty = cell(r, map.counterparty) || null;
    const description = cell(r, map.description) || counterparty || '';
    const external_id = cell(r, map.id) || null;
    const raw: Record<string, unknown> = {};
    r.forEach((v, i) => { raw[header[i]?.trim() || `col_${i + 1}`] = v; });
    out.push({ posted_date, amount_cents, description, counterparty, external_id, raw });
  }
  return out;
}
